/* eslint-disable import/no-cycle */
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import {
  BillingOverview,
  PlansTiersResponse,
  ShopOffer,
  SmsPricingResponse,
} from '@/types/billing-types';

/**
 * Normalised result returned by every structural action endpoint
 * (subscribe, confirm, cancel, undo, extend trial, repay).
 * Matches SubscriptionActionResult in the backend.
 */
export type ActionResult =
  | { status: 'completed'; requiresAuthRefresh?: boolean }
  | { status: 'action_required'; action: Record<string, any> }
  | { status: 'pending_shopify_approval'; confirmationUrl: string; chargeId: string }
  | { status: 'failed'; error: string };

// ─────────────────────────────────────────────────────────────────────────
// idShop is REQUIRED on every /billing/* call: the backend PermissionGuard
// compares it against the JWT's idShop (403 on mismatch) and ParseIntPipe
// rejects the request when it is missing. It travels in the query string for
// GET/PATCH/DELETE and in the body for POST.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Adyen payment methods (whitelist) by billing flow. Reused by every dialog
 * that mounts {@link ReviewAndPayCard} or {@link PaymentBlock} so the rule
 * lives in one place :
 *   - Plan flows (subscribe, change plan, migrate V5) accept CB, PayPal and
 *     SEPA direct debit (« virement bancaire »). Long-running commitment
 *     justifies the broader set.
 *   - One-shot flows (repay invoice) accept CB and PayPal only. SEPA isn't
 *     suited to instant one-shot operations because of its delayed settlement
 *     (D+2/D+5) — the customer would think the payment succeeded while the
 *     merchant is still waiting for funds.
 *   - SMS credits top-up accepts CB only, see {@link ALLOWED_METHODS_TOPUP}.
 */
export const ALLOWED_METHODS_PLAN = ['scheme', 'paypal', 'sepadirectdebit'];
export const ALLOWED_METHODS_ONESHOT = ['scheme', 'paypal'];

/**
 * SMS credits top-up: CB only, PayPal deliberately withheld.
 *
 * PayPal was offered here but never worked: Adyen answers `/payments` with a
 * `Pending` result and an `sdk` action carrying the PayPal order token, and
 * the back used to report that as a refusal — the popup stayed on a spinner
 * and the offer was left `Open` in Adyen for good. The back now forwards the
 * action, but the PayPal leg of this flow has never completed end to end
 * (`resultCode: Error` on every attempt in TEST), so it stays off until it is
 * validated on a deployed environment.
 *
 * Restoring it = using {@link ALLOWED_METHODS_ONESHOT} here again. Nothing
 * else to revert.
 */
export const ALLOWED_METHODS_TOPUP = ['scheme'];

function currentIdShop(): number {
  return UserState.activeShop?.id ?? 0;
}

/** Query string carrying `idShop` (+ optional extra params). */
function idShopQuery(extra?: Record<string, string>): string {
  const params = new URLSearchParams({
    idShop: String(currentIdShop()),
    ...(extra ?? {}),
  });
  return `?${params.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Type-guards — defensive parsing so a malformed response never crashes the
// component tree. Returns null on shape mismatch, the store treats it as a
// loading failure and the UI shows a fallback message.
// ─────────────────────────────────────────────────────────────────────────

function isShopOffer(value: unknown): value is ShopOffer {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return 'offerVersion' in v && typeof v.canMigrateToV5 === 'boolean';
}

function isBillingOverview(value: unknown): value is BillingOverview {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return 'shop' in v && 'plan' in v && 'wallet' in v;
}

/**
 * Thin wrappers around the /billing/* endpoints exposed by apps/payments.
 *
 * Conventions:
 *   - Every call uses NestApiType.PAYMENTS (VUE_APP_NESTJS_PAYMENT_API_URL).
 *   - JWT is added automatically by nestCall from the cookie.
 *   - On HTTP error the wrapper returns null (callers handle the absence).
 *
 * Read-only (P0) endpoints are exposed here. Write endpoints (P1) come in
 * P3 alongside the dialogs (ChangePlan, Unsubscribe, etc.).
 */

export async function fetchOfferVersion(): Promise<ShopOffer | null> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    '/billing/offer-version',
    {},
    idShopQuery(),
  );
  return isShopOffer(result) ? result : null;
}

export async function fetchOverview(): Promise<BillingOverview | null> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    '/billing/overview',
    {},
    idShopQuery(),
  );
  return isBillingOverview(result) ? result : null;
}

export async function fetchSmsPricing(
  input: { planOverride?: string } = {},
): Promise<SmsPricingResponse | null> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    '/billing/sms-pricing',
    {},
    idShopQuery(input.planOverride ? { planOverride: input.planOverride } : undefined),
  );
  return result && typeof result === 'object' && 'countries' in result
    ? (result as SmsPricingResponse)
    : null;
}

/**
 * Discriminated result for `/billing/plans/tiers`. The back returns a
 * structured 400 with `code: 'PAGES_VIEWED_UNAVAILABLE'` for V4 shops whose
 * page-view stats are not yet computed — the front catches it to display a
 * "contact support" state in the Change Plan modal.
 */
export type PlansTiersResult =
  | PlansTiersResponse
  | { error: { code?: string; message?: string } };

export async function fetchPlansTiers(
  input: { overrideOffer?: 'v4' | 'v5' } = {},
): Promise<PlansTiersResult | null> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    '/billing/plans/tiers',
    {},
    idShopQuery(input.overrideOffer ? { overrideOffer: input.overrideOffer } : undefined),
  );
  if (!result || typeof result !== 'object') return null;
  if ('error' in result) return result as { error: { code?: string; message?: string } };
  if ('tiers' in result) return result as PlansTiersResponse;
  return null;
}

export async function previewPlan(input: {
  plan: string;
  frequency: 'monthly' | 'yearly';
  targetTier?: number;
  smsInclusion?: number;
}): Promise<any | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/plans/preview',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) ? result : null;
}

export async function previewV5Migration(input: {
  plan: string;
  frequency: 'monthly' | 'yearly';
}): Promise<any | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/plans/preview-v5-migration',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) ? result : null;
}

// `fetchInvoices` removed — `GET /billing/invoices` is now consumed
// directly by SpmTableAPI (see `BillsList.vue`). The component drives the
// `StandardQueryListing` contract (filters / order / limit / offset)
// natively and writes its results into its own internal state.

/**
 * Downloads an invoice PDF through the authenticated nestCall path, then
 * triggers a client-side save via an object URL.
 *
 * Why not just `window.open(`${baseUrl}/billing/invoices/:id/pdf`)`?
 * That's a browser navigation — it does NOT carry the `Authorization: Bearer`
 * header set by our axios interceptor, so the request lands on NestJS without
 * a JWT and `AuthGuard('jwt')` returns 401. Fetching as a blob via nestCall
 * keeps the request authenticated; we then trigger the download client-side.
 *
 * Returns `false` on any failure (network, 401/404, Prestashop proxy error).
 */
export async function downloadInvoicePdf(id: string): Promise<boolean> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    `/billing/invoices/${encodeURIComponent(id)}/pdf`,
    {},
    idShopQuery(),
    undefined,
    true,
    {},
    { responseType: 'blob' },
  );
  if (!result || typeof result !== 'object' || !('data' in result)) {
    return false;
  }
  const blob = (result as { data: Blob }).data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

// ─────────────────────────────────────────────────────────────────────────
// Write endpoints (P1) — every action returns an ActionResult that the front
// inspects to decide what to do next (reload, 3DS, Shopify redirect, error).
// ─────────────────────────────────────────────────────────────────────────
async function callAction(
  path: string,
  body: Record<string, any>,
): Promise<ActionResult> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    path,
    {},
    '',
    { idShop: currentIdShop(), ...body },
  );
  if (!result || typeof result !== 'object') {
    return { status: 'failed', error: 'No response from server' };
  }
  if ('error' in result) {
    const err = (result as { error: any }).error;
    return {
      status: 'failed',
      error: typeof err === 'string' ? err : err?.message ?? 'Unknown error',
    };
  }
  if ('status' in result) {
    return result as ActionResult;
  }
  return { status: 'failed', error: 'Unrecognised response shape' };
}

export async function cancelPlan(input: {
  reason?: string;
  comment?: string;
} = {}): Promise<ActionResult> {
  return callAction('/billing/plans/cancel', input);
}

export async function undoCancelPlan(): Promise<ActionResult> {
  return callAction('/billing/plans/cancel/undo', {});
}

export async function downgradeNextPlan(input: {
  nextPlan: string;
  /**
   * Monthly SMS amount that should apply at the next renewal :
   *   - `undefined`  → don't touch the current SMS subscription
   *   - `0`          → explicitly remove SMS at next renewal
   *   - `> 0`        → set SMS to this amount at next renewal
   *
   * Always sent by `ReviewAndPayStep` (path downgrade) from the wizard's
   * `selectedSmsInclusion` ref so the user's SMS choice at the AddCreditsStep
   * is not silently dropped — fix for the « downgrade ignores SMS » bug.
   */
  nextSmsPlan?: number;
}): Promise<ActionResult> {
  return callAction('/billing/plans/downgrade-next', input);
}

export async function undoDowngradeNextPlan(): Promise<ActionResult> {
  return callAction('/billing/plans/downgrade-next/undo', {});
}

export async function extendTrial(): Promise<ActionResult> {
  return callAction('/billing/trial/extend', {});
}

export async function setDefaultPaymentMethod(id: string): Promise<{ ok: true } | null> {
  const result = await nestCall(
    'patch',
    NestApiType.PAYMENTS,
    `/billing/payment-methods/${encodeURIComponent(id)}/default`,
    {},
    idShopQuery(),
  );
  return result && typeof result === 'object' && 'ok' in result
    ? (result as { ok: true })
    : null;
}

export async function deletePaymentMethod(id: string): Promise<{ ok: true } | null> {
  const result = await nestCall(
    'delete',
    NestApiType.PAYMENTS,
    `/billing/payment-methods/${encodeURIComponent(id)}`,
    {},
    idShopQuery(),
  );
  return result && typeof result === 'object' && 'ok' in result
    ? (result as { ok: true })
    : null;
}

/**
 * Returns the Adyen `paymentMethods` response that the front feeds into
 * `AdyenCheckout({ paymentMethodsResponse })`. Defaults to a 0€ zero-auth
 * configuration in EUR / fr_FR when no input is provided.
 */
export async function fetchDropinConfig(input: {
  amount?: number;
  currency?: string;
  countryCode?: string;
  locale?: string;
} = {}): Promise<any | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/payment-methods/dropin-config',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) ? result : null;
}

/**
 * Tokenises a new card without any debit (0€ zero-auth payment with
 * storePaymentMethod). The backend handles 3DS via the standard
 * `action_required` flow.
 */
export async function addPaymentMethodApi(
  adyenStateData: Record<string, any>,
): Promise<ActionResult> {
  return callAction('/billing/payment-methods/add', { adyenStateData });
}

/**
 * Completes the 3DS challenge for an add-card flow.
 */
export async function confirmAddPaymentMethodApi(
  adyenStateData: Record<string, any>,
): Promise<ActionResult> {
  return callAction('/billing/payment-methods/add/confirm', { adyenStateData });
}

export async function setWalletAutoRecharge(input: {
  enabled: boolean;
  threshold?: number;
  rechargeAmount?: number;
  country?: string;
}): Promise<{ ok: true } | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/wallet/auto-recharge',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) && 'ok' in result
    ? (result as { ok: true })
    : null;
}

/**
 * Règle d'auto-exclusion segmentée de facturation (offre V5). Lit/écrit la
 * config côté payments (`/billing/auto-exclusion`). `segmentsConfig` est la
 * config du SegmentationsPanel (SegmentOrConditionBlock[][]).
 */
export interface BillingAutoExclusionConfig {
  isActive: boolean;
  segmentsConfig: any;
  excludedCount: number;
}

export async function fetchBillingAutoExclusion(): Promise<BillingAutoExclusionConfig | null> {
  const result = await nestCall(
    'get',
    NestApiType.PAYMENTS,
    '/billing/auto-exclusion',
    {},
    idShopQuery(),
  );
  return result && typeof result === 'object' && 'isActive' in result
    ? (result as BillingAutoExclusionConfig)
    : null;
}

export async function setBillingAutoExclusion(input: {
  isActive: boolean;
  segmentsConfig: any;
}): Promise<{ ok: true; excludedCount: number } | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/auto-exclusion',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) && 'ok' in result
    ? (result as { ok: true; excludedCount: number })
    : null;
}

export async function walletTopUp(input: {
  country: string;
  smsCount: number;
  adyenStateData?: Record<string, any>;
  /** DB id of the saved card to use. Defaults to the user's default card. */
  paymentMethodId?: string;
}): Promise<ActionResult> {
  return callAction('/billing/wallet/top-up', input);
}

export async function subscribePlan(input: {
  plan: string;
  frequency: 'monthly' | 'yearly';
  adyenStateData?: Record<string, any>;
  paymentMethodId?: string;
  migrateToV5?: boolean;
  targetTier?: number;
  smsInclusion?: number;
  /**
   * V5 auto-tier-upgrade configuration. Ignored for V4 (no tier system).
   * Persisted into `shops_plans.plan` after a successful subscribe so the
   * renewal cron can recompute the tier each month within the user-defined
   * `tierAutoUpgradeMax` cap.
   */
  tierAutoUpgradeEnabled?: boolean;
  tierAutoUpgradeMax?: number;
}): Promise<ActionResult> {
  return callAction('/billing/plans/subscribe', input);
}

export async function confirmPlan(input: {
  adyenStateData?: Record<string, any>;
  chargeId?: string;
}): Promise<ActionResult> {
  return callAction('/billing/plans/confirm', input);
}

export async function repayInvoice(
  id: string,
  adyenStateData?: Record<string, any>,
): Promise<ActionResult> {
  return callAction(
    `/billing/invoices/${encodeURIComponent(id)}/pay`,
    { adyenStateData },
  );
}

export async function bumpShopifyCappedAmount(input: {
  targetCap?: number;
} = {}): Promise<any | null> {
  const result = await nestCall(
    'post',
    NestApiType.PAYMENTS,
    '/billing/shopify/bump-capped-amount',
    {},
    '',
    { idShop: currentIdShop(), ...input },
  );
  return result && !('error' in result) ? result : null;
}
